import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Receipt, Loader2, Plus, X, Send, Download, EyeOff, Eye, Ban, RefreshCw, Sparkles, ExternalLink, Copy, Search, ShieldCheck, Lock, AlertTriangle } from "lucide-react";
import { RailSelector, RailChip, PhonePeBadge } from "./payments/RailSelector";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const HEADER_META = {
  trade:      { label: "Trade",      accent: "#B87333", desc: "Cement, steel & aggregates" },
  service:    { label: "Service",    accent: "#2FB999", desc: "Certificate fees + paid quotes" },
  commission: { label: "Commission", accent: "#D8B23E", desc: "Settlement payouts" },
  custom:     { label: "Custom",     accent: "#8A8A82", desc: "Admin-typed dynamic header" },
};

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso || "—"; } };


/**
 * AdminInvoiceHub — the central invoicing panel used inside the Admin
 * dashboard tab of the same name.  Renders the full list with header +
 * status filters, a live roll-up stats strip, and per-row actions:
 * download PDF, resend email, toggle GST mask, void, and copy the
 * Razorpay payment link.  A prominent "Ad-hoc Invoice" CTA opens the
 * builder dialog.
 */
export function AdminInvoiceHub({ api }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_paid: 0, total_due: 0, count: 0, by_header: {} });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ header: "", status: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.header) params.set("header", filters.header);
      if (filters.status) params.set("status", filters.status);
      const r = await api.get("/admin/invoices?" + params.toString());
      setRows(r.data?.invoices || []);
      setStats(r.data?.stats || { total_paid: 0, total_due: 0, count: 0, by_header: {} });
    } catch (e) { toast.error("Could not load invoices"); }
    finally { setLoading(false); }
  }, [api, filters.header, filters.status]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.invoice_number || "").toLowerCase().includes(q) ||
      (r.buyer_business || "").toLowerCase().includes(q) ||
      (r.buyer_email || "").toLowerCase().includes(q) ||
      (r.razorpay_payment_id || "").toLowerCase().includes(q),
    );
  }, [rows, filters.q]);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = (inv) => `${backend}/api/invoices/${inv.id}/pdf`;

  const runBackfill = async () => {
    if (!window.confirm("Sweep every existing paid order / certificate / paid quote into the Invoicing Hub?\n\nThis is idempotent — rows that already have an invoice will be skipped.")) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/invoices/backfill");
      const { trade, service, commission, skipped } = r.data?.inserted || {};
      toast.success(`Backfill complete · Trade ${trade || 0} · Service ${service || 0} · Commission ${commission || 0} · Skipped ${skipped || 0}`);
      load();
    } catch { toast.error("Backfill failed"); }
    finally { setLoading(false); }
  };

  const resend = async (inv) => {
    setBusyId(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/resend`);
      toast.success(`Invoice ${inv.invoice_number} re-emailed to ${inv.buyer_email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
    finally { setBusyId(null); }
  };
  const toggleMask = async (inv) => {
    setBusyId(inv.id);
    try {
      const r = await api.post(`/admin/invoices/${inv.id}/mask`);
      toast.success(r.data?.gst_masked ? "GSTIN + PAN masked on PDF" : "GSTIN + PAN now visible on PDF");
      load();
    } catch { toast.error("Mask toggle failed"); }
    finally { setBusyId(null); }
  };
  const voidInvoice = async (inv) => {
    const reason = window.prompt(`Void ${inv.invoice_number}?\n\nOptional note (kept for audit):`, "");
    if (reason === null) return;
    setBusyId(inv.id);
    try {
      await api.post(`/admin/invoices/${inv.id}/void`, { reason });
      toast.success(`${inv.invoice_number} voided`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Void failed"); }
    finally { setBusyId(null); }
  };

  // Executive Mark as Paid — final override for pending invoices.
  // Non-reversible: once paid (by webhook OR manual), the button is
  // hidden and the record is locked as a verified transaction.
  const [markPayTarget, setMarkPayTarget] = useState(null);
  const [markPayReason, setMarkPayReason] = useState("");
  const openMarkPay = (inv) => { setMarkPayTarget(inv); setMarkPayReason(""); };
  const confirmMarkPay = async () => {
    if (!markPayTarget) return;
    setBusyId(markPayTarget.id);
    try {
      const r = await api.post(`/admin/invoices/${markPayTarget.id}/mark-paid`,
                                { reason: markPayReason });
      toast.success(r.data?.message
        || `${markPayTarget.invoice_number} marked PAID & locked`);
      setMarkPayTarget(null); setMarkPayReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally { setBusyId(null); }
  };
  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Payment link copied");
  };

  return (
    <div className="text-[#F5F5F0]" data-testid="admin-invoice-hub">
      {/* Header + primary CTAs */}
      <div className="mb-6">
        <div className="overline text-[#B87333] mb-2 inline-flex items-center gap-2">
          <Receipt size={12}/> Invoicing Hub
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">Centralised billing</h2>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              One place to raise, send and reconcile every invoice — Trade orders, Service fees, Commission payouts and one-off ad-hoc bills.  Razorpay-synced, GST-compliant, downloadable as a signed PDF.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="invoice-backfill-btn" onClick={runBackfill} disabled={loading}
                    className="rounded-full border border-white/15 hover:border-[#B87333] text-[#C9C9C2] hover:text-[#B87333] px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-2">
              <RefreshCw size={12}/> Backfill legacy
            </button>
            <button data-testid="invoice-adhoc-btn" onClick={() => setDialogOpen(true)}
                    className="rounded-full bg-[#B87333] text-black px-4 py-2 text-[10px] uppercase tracking-[0.24em] font-grotesk hover:brightness-110 inline-flex items-center gap-2">
              <Plus size={12}/> Ad-hoc Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="invoice-stats">
        <StatCard label="Total paid" value={rupee(stats.total_paid)} accent="#2FB999" testid="stat-paid" />
        <StatCard label="Outstanding" value={rupee(stats.total_due)} accent="#D8B23E" testid="stat-due" />
        <StatCard label="Count" value={stats.count} accent="#B87333" testid="stat-count" />
        <StatCard label="Headers" value={Object.keys(stats.by_header || {}).length} accent="#8A8A82" testid="stat-headers" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="invoice-filters">
        <div className="flex rounded-full border border-white/10 overflow-hidden">
          {["", "trade", "service", "commission", "custom"].map((h) => (
            <button key={h || "all"} onClick={() => setFilters((f) => ({ ...f, header: h }))}
                    data-testid={`filter-header-${h || "all"}`}
                    className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.22em] transition-colors ${
                      filters.header === h ? "bg-[#B87333] text-black" : "text-[#8A8A82] hover:text-[#F5F5F0]"
                    }`}>
              {h || "All"}
            </button>
          ))}
        </div>
        <div className="flex rounded-full border border-white/10 overflow-hidden">
          {["", "paid", "sent", "draft", "void"].map((s) => (
            <button key={s || "any"} onClick={() => setFilters((f) => ({ ...f, status: s }))}
                    data-testid={`filter-status-${s || "any"}`}
                    className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.22em] transition-colors ${
                      filters.status === s ? "bg-[#2FB999] text-black" : "text-[#8A8A82] hover:text-[#F5F5F0]"
                    }`}>
              {s || "Any status"}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A82]"/>
          <input data-testid="invoice-search" placeholder="Search #, buyer or Razorpay ID"
                 value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 className="w-full bg-black/40 border border-white/15 rounded-full pl-8 pr-3 py-1.5 font-body text-xs text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-sm p-12 text-center" data-testid="invoice-empty">
          <Receipt size={22} className="text-[#8A8A82] mx-auto mb-3" />
          <p className="font-serif text-lg">No invoices found.</p>
          <p className="font-body text-xs text-[#8A8A82] mt-1">Run "Backfill legacy" to import existing paid orders, or click "Ad-hoc Invoice" to raise a fresh bill.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="invoice-list">
          {filtered.map((inv) => {
            const meta = HEADER_META[inv.header] || HEADER_META.custom;
            const statusColor = { paid: "#2FB999", sent: "#D8B23E", draft: "#8A8A82", void: "#C0392B" }[inv.status || "draft"] || "#8A8A82";
            return (
              <motion.div key={inv.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                          data-testid={`invoice-row-${inv.id}`}
                          className="border border-white/10 rounded-sm p-4 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border"
                            style={{ color: meta.accent, borderColor: meta.accent + "55" }}>
                        {(inv.custom_header_label || meta.label).toUpperCase()}
                      </span>
                      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border"
                            style={{ color: statusColor, borderColor: statusColor + "55" }}
                            data-testid={`invoice-status-${inv.id}`}>
                        {(inv.status || "draft").toUpperCase()}
                      </span>
                      {inv.gst_masked && <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C0392B]">GST + PAN MASKED</span>}
                    </div>
                    <div className="mt-1 font-serif text-lg tabular-nums" data-testid={`invoice-number-${inv.id}`}>{inv.invoice_number}</div>
                    <div className="font-body text-xs text-[#8A8A82] mt-0.5">
                      {inv.buyer_business || inv.buyer_email} · {fmtDate(inv.issued_at)}
                      {inv.razorpay_payment_id && <> · <span className="text-[#4285F4]">RZP: {inv.razorpay_payment_id}</span></>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-2xl tabular-nums" data-testid={`invoice-total-${inv.id}`}>{rupee(inv.total)}</div>
                    {(inv.cgst || inv.sgst || inv.igst) > 0 && (
                      <div className="font-body text-[10px] text-[#8A8A82] mt-0.5">
                        Sub {rupee(inv.subtotal)} · GST {rupee((inv.cgst||0)+(inv.sgst||0)+(inv.igst||0))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/[0.06]">
                  <a href={pdfUrl(inv)} target="_blank" rel="noreferrer"
                     data-testid={`invoice-pdf-${inv.id}`}
                     className="rounded-full border border-white/15 hover:border-[#B87333] text-[#C9C9C2] hover:text-[#B87333] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                    <Download size={11}/> PDF
                  </a>
                  {inv.status !== "void" && (
                    <button data-testid={`invoice-resend-${inv.id}`} onClick={() => resend(inv)} disabled={busyId === inv.id}
                            className="rounded-full border border-white/15 hover:border-[#2FB999] text-[#C9C9C2] hover:text-[#2FB999] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                      {busyId === inv.id ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>} Resend
                    </button>
                  )}
                  <button data-testid={`invoice-mask-${inv.id}`} onClick={() => toggleMask(inv)} disabled={busyId === inv.id}
                          className="rounded-full border border-white/15 hover:border-[#D8B23E] text-[#C9C9C2] hover:text-[#D8B23E] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                    {inv.gst_masked ? <><Eye size={11}/> Unmask GSTIN+PAN</> : <><EyeOff size={11}/> Mask GSTIN+PAN</>}
                  </button>
                  {inv.razorpay_payment_link_url && !["paid", "void"].includes(inv.status) && (
                    <>
                      <button data-testid={`invoice-copylink-${inv.id}`} onClick={() => copyLink(inv.razorpay_payment_link_url)}
                              className={`rounded-full border hover:border-[#4285F4] text-[#C9C9C2] hover:text-[#4285F4] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5 ${(inv.payment_rail || "razorpay") === "razorpay" ? "border-[#D8B23E]/50" : "border-white/15"}`}>
                        <Copy size={11}/> Copy Pay Link
                      </button>
                      <a href={inv.razorpay_payment_link_url} target="_blank" rel="noreferrer"
                         className="rounded-full border border-white/15 hover:border-[#4285F4] text-[#C9C9C2] hover:text-[#4285F4] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                        <ExternalLink size={11}/> Open
                      </a>
                    </>
                  )}
                  {inv.phonepe_payment_link_url && !["paid", "void"].includes(inv.status) && (
                    <button data-testid={`invoice-copy-phonepe-${inv.id}`} onClick={() => copyLink(inv.phonepe_payment_link_url)}
                            className={`rounded-full border hover:border-[#5F259F] text-[#C9C9C2] hover:text-[#C9A6F0] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5 ${inv.payment_rail === "phonepe" ? "border-[#D8B23E]/50" : "border-white/15"}`}>
                      <PhonePeBadge size={13} /> <Copy size={11}/> Copy PhonePe Link
                    </button>
                  )}
                  {(inv.payment_rail || inv.payment_provider) && (
                    <RailChip rail={inv.status === "paid" ? (inv.payment_provider || (inv.razorpay_payment_id ? "razorpay" : inv.payment_rail)) : (inv.payment_rail || "razorpay")}
                              className="self-center" />
                  )}
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button data-testid={`invoice-mark-paid-${inv.id}`} onClick={() => openMarkPay(inv)} disabled={busyId === inv.id}
                            className="rounded-full border border-[#2FB999]/60 bg-[#2FB999]/10 hover:bg-[#2FB999]/25 text-[#2FB999] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                      <ShieldCheck size={11}/> Executive Mark as Paid
                    </button>
                  )}
                  {inv.status === "paid" && (
                    <span data-testid={`invoice-locked-${inv.id}`}
                          className="rounded-full border border-[#2FB999]/40 bg-[#2FB999]/5 text-[#2FB999] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5"
                          title={inv.paid_source === "manual_admin"
                            ? `Manually marked paid by ${inv.manual_paid_by_email || "admin"} · locked`
                            : "Auto-verified via Razorpay webhook · locked"}>
                      <Lock size={11}/> Locked · {inv.paid_source === "manual_admin" ? "Manual" : "Auto-verified"}
                    </span>
                  )}
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button data-testid={`invoice-void-${inv.id}`} onClick={() => voidInvoice(inv)} disabled={busyId === inv.id}
                            className="rounded-full border border-white/15 hover:border-[#C0392B] text-[#C9C9C2] hover:text-[#C0392B] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-grotesk inline-flex items-center gap-1.5">
                      <Ban size={11}/> Void
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AdhocInvoiceDialog api={api} open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={() => { setDialogOpen(false); load(); }} />

      {/* Executive Mark as Paid · confirmation */}
      <AlertDialog open={!!markPayTarget} onOpenChange={(o) => { if (!o) { setMarkPayTarget(null); setMarkPayReason(""); }}}>
        <AlertDialogContent data-testid="mark-paid-confirm-dialog"
                            className="bg-[#0a0a0a] border border-[#2FB999]/40 text-[#F5F5F0]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#D8B23E]" />
              Executive override · mark PAID?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="font-body text-sm text-[#C9C9C2] leading-relaxed pt-2 space-y-3">
                <div>
                  You are about to manually mark{" "}
                  <b className="text-[#F5F5F0]">{markPayTarget?.invoice_number}</b>{" "}
                  ({rupee(markPayTarget?.total)}) as <b className="text-[#2FB999]">PAID</b>{" "}
                  outside of Razorpay's automated capture.
                </div>
                <div className="border border-[#C0392B]/40 bg-[#1B0A0A]/60 rounded-sm p-3 text-[12px]">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C0392B] mb-1">
                    ⚠ Non-reversible
                  </div>
                  Once you confirm, the invoice becomes a locked, verified
                  ledger row. The Executive Override button will disappear
                  and this row cannot be flipped back to pending — only a
                  credit note or void (before this action) can undo it.
                </div>
                <div>
                  <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">
                    Reason / bank ref (kept in audit log)
                  </label>
                  <input data-testid="mark-paid-reason"
                          value={markPayReason}
                          onChange={(e) => setMarkPayReason(e.target.value)}
                          placeholder="e.g. NEFT UTR 987654321 · confirmed with buyer over call"
                          className="w-full bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#2FB999]" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-paid-cancel"
                                className="bg-transparent border border-white/20 text-[#C9C9C2] hover:bg-white/[0.06]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="mark-paid-confirm-yes"
                                onClick={confirmMarkPay}
                                disabled={busyId === markPayTarget?.id}
                                className="bg-[#2FB999] hover:brightness-110 text-black font-grotesk uppercase tracking-[0.18em] text-[10px]">
              {busyId === markPayTarget?.id ? "Locking…" : "Yes, mark PAID & lock"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function StatCard({ label, value, accent, testid }) {
  return (
    <div className="border rounded-sm p-4" style={{ borderColor: accent + "55" }} data-testid={testid}>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.24em]" style={{ color: accent }}>{label}</div>
      <div className="font-serif text-2xl mt-1 tabular-nums text-[#F5F5F0]">{value}</div>
    </div>
  );
}


function AdhocInvoiceDialog({ api, open, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    header: "service", custom_header_label: "",
    buyer_id: "", buyer_email: "", buyer_business: "", buyer_state: "", buyer_gstin: "", buyer_phone: "",
    line_items: [{ desc: "", qty: 1, unit_rate: 0 }],
    gst_pct: 18, discount: 0, notes: "", generate_payment_link: true, payment_rail: "razorpay",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "admin")))
      .catch(() => setUsers([]));
  }, [open, api]);

  const total = useMemo(() => {
    const sub = form.line_items.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_rate) || 0), 0);
    const taxable = Math.max(0, sub - Number(form.discount || 0));
    const gst = taxable * (Number(form.gst_pct) || 0) / 100;
    return { subtotal: sub, taxable, gst, total: taxable + gst };
  }, [form.line_items, form.gst_pct, form.discount]);

  const updateItem = (i, patch) => {
    setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => idx === i ? { ...li, ...patch } : li) }));
  };
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { desc: "", qty: 1, unit_rate: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const pickBuyer = (uid) => {
    const u = users.find((x) => x.id === uid);
    if (!u) return setForm((f) => ({ ...f, buyer_id: "" }));
    setForm((f) => ({
      ...f, buyer_id: u.id, buyer_email: u.email,
      buyer_business: u.profile?.business_name || u.name || "",
      buyer_state: u.profile?.state || "",
      buyer_gstin: u.profile?.gst_number || "",
      buyer_phone: u.profile?.phone || "",
    }));
  };

  const submit = async () => {
    if (!form.buyer_email && !form.buyer_id) { toast.error("Pick a buyer or type an email"); return; }
    if (form.line_items.filter((x) => x.desc && x.unit_rate > 0).length === 0) { toast.error("Add at least one valid line item"); return; }
    if (form.header === "custom" && !form.custom_header_label.trim()) { toast.error("Type the custom header label"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.filter((x) => x.desc),
        gst_pct: Number(form.gst_pct) || 0,
        discount: Number(form.discount) || 0,
      };
      const r = await api.post("/admin/invoices/adhoc", payload);
      toast.success(`Invoice ${r.data.invoice_number} raised · buyer emailed`);
      onCreated?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invoice"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[95]" data-testid="adhoc-invoice-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-[600px] bg-[#070707] border-l border-[#B87333]/30 overflow-y-auto">
        <div className="sticky top-0 bg-[#070707] px-6 py-4 border-b border-white/10 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk flex items-center gap-2">
              <Sparkles size={12}/> New ad-hoc invoice
            </div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">Bill any customer · any header</div>
          </div>
          <button onClick={onClose} data-testid="adhoc-close" className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Header + custom label */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Header *</label>
            <div className="flex flex-wrap gap-2" data-testid="adhoc-header-picker">
              {Object.entries(HEADER_META).map(([k, m]) => (
                <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, header: k }))}
                        data-testid={`adhoc-header-${k}`}
                        className={`rounded-full px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.22em] border transition-colors ${
                          form.header === k ? "border-current bg-white/[0.04]" : "border-white/10 text-[#8A8A82] hover:border-white/30"
                        }`}
                        style={form.header === k ? { color: m.accent } : {}}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#8A8A82] mt-1">{HEADER_META[form.header].desc}</div>
          </div>
          {form.header === "custom" && (
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Custom header label *</label>
              <input data-testid="adhoc-custom-label" value={form.custom_header_label}
                     onChange={(e) => setForm((f) => ({ ...f, custom_header_label: e.target.value }))}
                     placeholder="e.g. Advisory · Logistics · Consultancy"
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
              <div className="text-[10px] text-[#8A8A82] mt-1">Series will look like <span className="text-[#B87333]">VNC/&lt;code&gt;/YYYY/00001</span>.</div>
            </div>
          )}

          {/* Buyer */}
          <div>
            <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Buyer</label>
            <select data-testid="adhoc-buyer-select" value={form.buyer_id}
                    onChange={(e) => pickBuyer(e.target.value)}
                    className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]">
              <option value="">— Type email below OR pick a registered buyer —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {(u.profile?.business_name || u.name || u.email)}
                  {u.vendor_code ? ` · ${u.vendor_code}` : ""} · {u.email}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input data-testid="adhoc-buyer-email" value={form.buyer_email}
                     onChange={(e) => setForm((f) => ({ ...f, buyer_email: e.target.value }))}
                     placeholder="Buyer email *"
                     className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
              <input data-testid="adhoc-buyer-business" value={form.buyer_business}
                     onChange={(e) => setForm((f) => ({ ...f, buyer_business: e.target.value }))}
                     placeholder="Business / Bill to"
                     className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
              <input data-testid="adhoc-buyer-state" value={form.buyer_state}
                     onChange={(e) => setForm((f) => ({ ...f, buyer_state: e.target.value }))}
                     placeholder="State (place of supply)"
                     className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
              <input data-testid="adhoc-buyer-gstin" value={form.buyer_gstin}
                     onChange={(e) => setForm((f) => ({ ...f, buyer_gstin: e.target.value.toUpperCase() }))}
                     placeholder="Buyer GSTIN (optional)"
                     className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Line items *</label>
              <button type="button" data-testid="adhoc-add-item" onClick={addItem}
                      className="text-[10px] uppercase tracking-[0.22em] text-[#B87333] hover:brightness-125 inline-flex items-center gap-1">
                <Plus size={11}/> Add
              </button>
            </div>
            <div className="space-y-2" data-testid="adhoc-line-items">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_100px_36px] gap-2">
                  <input data-testid={`adhoc-item-desc-${i}`} value={li.desc}
                         onChange={(e) => updateItem(i, { desc: e.target.value })}
                         placeholder="Description"
                         className="bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
                  <input data-testid={`adhoc-item-qty-${i}`} type="number" min="0" step="0.01" value={li.qty}
                         onChange={(e) => updateItem(i, { qty: e.target.value })}
                         className="bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333] text-right" />
                  <input data-testid={`adhoc-item-rate-${i}`} type="number" min="0" step="0.01" value={li.unit_rate}
                         onChange={(e) => updateItem(i, { unit_rate: e.target.value })}
                         placeholder="Rate ₹"
                         className="bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333] text-right" />
                  <button type="button" data-testid={`adhoc-remove-item-${i}`} onClick={() => removeItem(i)} disabled={form.line_items.length === 1}
                          className="text-[#8A8A82] hover:text-[#C0392B] disabled:opacity-30">
                    <X size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Tax + discount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">GST %</label>
              <input data-testid="adhoc-gst-pct" type="number" min="0" max="28" step="0.01" value={form.gst_pct}
                     onChange={(e) => setForm((f) => ({ ...f, gst_pct: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            </div>
            <div>
              <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Discount ₹</label>
              <input data-testid="adhoc-discount" type="number" min="0" step="0.01" value={form.discount}
                     onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
            </div>
          </div>

          {/* Live total */}
          <div className="rounded-sm border border-[#B87333]/30 bg-gradient-to-br from-[#0e0a06] to-[#0a0a0a] p-4" data-testid="adhoc-total">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk mb-2">Live total</div>
            <div className="space-y-1 font-body text-[12px] text-[#C9C9C2]">
              <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{rupee(total.subtotal)}</span></div>
              {form.discount > 0 && <div className="flex justify-between"><span>Discount</span><span className="tabular-nums">− {rupee(form.discount)}</span></div>}
              <div className="flex justify-between"><span>GST @ {form.gst_pct}%</span><span className="tabular-nums">{rupee(total.gst)}</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk">Total</span>
              <span className="font-serif text-2xl text-[#F5F5F0] tabular-nums" data-testid="adhoc-total-value">{rupee(total.total)}</span>
            </div>
          </div>

          {/* Payment rail · admin decides at the point of action */}
          <RailSelector testId="adhoc-rail" value={form.payment_rail} onChange={(r) => setForm((f) => ({ ...f, payment_rail: r }))}
                        label="Payment Rail · link emailed to buyer" />

          {/* Payment link toggle */}
          <label className="flex items-center gap-2 text-xs text-[#C9C9C2] font-body cursor-pointer" data-testid="adhoc-genlink-label">
            <input data-testid="adhoc-genlink" type="checkbox" checked={form.generate_payment_link}
                   onChange={(e) => setForm((f) => ({ ...f, generate_payment_link: e.target.checked }))}
                   className="accent-[#B87333]" />
            Generate both Razorpay and PhonePe pay links + QR — the buyer e-mail carries PAY VIA PHONEPE and PAY VIA RAZORPAY buttons; your pick only decides which rail the PDF/QR highlights.
          </label>

          <button data-testid="adhoc-submit" onClick={submit} disabled={busy}
                  className="w-full rounded-full bg-[#B87333] text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Raise Invoice & Email Buyer
          </button>
        </div>
      </div>
    </div>
  );
}
